import isNil from 'lodash/isNil';
import { forwardRef, type ComponentProps, type ReactNode } from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Typography, TypographyVariant } from '@ui/components/base/typography';
import { OverflowTypography } from '@ui/components/fields/overflowTypography';
import { Tooltip } from '@ui/components/tooltips/Tooltip';
import { cn } from '@ui/lib/utils';

const ROW_CLASSNAME = [
  'h-auto justify-start gap-2.5 rounded-lg px-2 py-1.5 text-left',
  'transition-colors duration-150',
  'hover:bg-fg-strong/4',
].join(' ');

const ROW_SELECTED_CLASSNAME = 'bg-accent-soft hover:bg-accent-soft';

const TEXT_GROUP_CLASSNAME = [
  'flex min-w-0 flex-1 flex-col transition-opacity duration-150',
  'group-data-[collapsed=true]/entity-row:w-0 group-data-[collapsed=true]/entity-row:flex-none',
  'group-data-[collapsed=true]/entity-row:overflow-hidden group-data-[collapsed=true]/entity-row:opacity-0',
  'group-data-[collapsed=true]/entity-row:pointer-events-none',
].join(' ');

const TRAILING_CLASSNAME = [
  'ml-auto flex shrink-0 items-center transition-opacity duration-150',
  'group-data-[collapsed=true]/entity-row:w-0 group-data-[collapsed=true]/entity-row:overflow-hidden',
  'group-data-[collapsed=true]/entity-row:opacity-0',
].join(' ');

interface SidebarEntityRowProps extends Omit<ComponentProps<typeof Button>, 'children'> {
  leading: ReactNode;
  primary: string;
  secondary?: string;
  trailing?: ReactNode;
  collapsed?: boolean;
  selected?: boolean;
  tooltip?: string;
  fullWidth?: boolean;
  primaryBold?: boolean;
}

const SidebarEntityRow = forwardRef<HTMLButtonElement, SidebarEntityRowProps>(
  (
    {
      leading,
      primary,
      secondary,
      trailing,
      collapsed = false,
      selected = false,
      tooltip,
      fullWidth = true,
      primaryBold = true,
      className,
      ...props
    },
    ref
  ) => {
    const row = (
      <Button
        ref={ref}
        variant={ButtonVariant.GHOST}
        data-collapsed={collapsed}
        className={cn(
          'group/entity-row',
          ROW_CLASSNAME,
          fullWidth && 'w-full',
          selected && ROW_SELECTED_CLASSNAME,
          className
        )}
        {...props}
      >
        <span className="flex shrink-0 items-center">{leading}</span>
        <span className={TEXT_GROUP_CLASSNAME}>
          <OverflowTypography
            variant={TypographyVariant.STRONG}
            text={primary}
            bold={primaryBold}
            className="leading-5"
          />
          {!isNil(secondary) ? (
            <Typography variant={TypographyVariant.MUTED} size="xs" className="truncate leading-4">
              {secondary}
            </Typography>
          ) : null}
        </span>
        {!isNil(trailing) ? <span className={TRAILING_CLASSNAME}>{trailing}</span> : null}
      </Button>
    );

    if (collapsed && !isNil(tooltip)) {
      return (
        <Tooltip content={tooltip} side="right" triggerClassName={fullWidth ? 'block w-full' : 'block'}>
          {row}
        </Tooltip>
      );
    }
    return row;
  }
);

SidebarEntityRow.displayName = 'SidebarEntityRow';

export { SidebarEntityRow };
export type { SidebarEntityRowProps };
